/* =====================================================================
 * Human-in-the-loop (interactive mode) — 순수 헬퍼.
 *
 * 사용자가 질문 카드에서 고른 내용을
 *   1) 대화에 그대로 남는 사람이 읽는 user 메시지 텍스트
 *   2) 서버가 재질문을 차단하는 데 쓰는 구조화 `HitlAnswer`
 * 두 가지로 만든다. 스토어 · UI · 테스트가 같은 규칙을 공유하도록
 * 컴포넌트 밖에 둔다.
 * ===================================================================== */

import type {
  AgentInProgressData,
  HitlAnswer,
  HitlAnswerValue,
  HitlResponseAcceptedDetail,
  UserInputOption,
  UserInputRequest,
} from '../types/agent';

export interface HitlSelection {
  selectedOptionIds: string[];
  freeText?: string;
  skipped?: boolean;
}

/** 선택된 옵션 id 를 표시 라벨로 되돌린다 (요청 페이로드 기준). */
export function resolveSelectedLabels(
  request: UserInputRequest,
  selectedOptionIds: string[],
): string[] {
  const byId = new Map(request.options.map((opt) => [opt.id, opt.label]));
  return selectedOptionIds
    .map((id) => byId.get(id))
    .filter((label): label is string => typeof label === 'string' && label.length > 0);
}

/** 전송용 구조화 응답을 만든다. */
export function buildHitlAnswer(
  request: UserInputRequest,
  selection: HitlSelection,
): HitlAnswer {
  const skipped = Boolean(selection.skipped);
  const optionById = new Map(request.options.map((option) => [option.id, option]));
  const selectedOptionIds = skipped ? [] : [...selection.selectedOptionIds];
  const unknownOptionId = selectedOptionIds.find((id) => !optionById.has(id));
  if (unknownOptionId) {
    throw new Error(`Unknown HITL option id: ${unknownOptionId}`);
  }
  if (new Set(selectedOptionIds).size !== selectedOptionIds.length) {
    throw new Error('Duplicate HITL option ids are not allowed');
  }
  const freeText = skipped ? '' : (selection.freeText ?? '').trim();

  const hasContinuationId = Boolean(request.continuation_id);
  const hasInputSchema = Boolean(request.pending_input_schema);
  const hasContinuationContext = Boolean(request.continuation_context);
  if (
    (hasContinuationId || hasInputSchema || hasContinuationContext) &&
    !(hasContinuationId && hasInputSchema && hasContinuationContext)
  ) {
    throw new Error('Incomplete HITL continuation contract');
  }

  let values: HitlAnswerValue[] = [];
  if (hasContinuationId) {
    const schema = request.pending_input_schema!;
    const context = request.continuation_context!;
    if (
      context.checkpoint_id !== request.continuation_id ||
      context.request_id !== request.request_id
    ) {
      throw new Error('Mismatched HITL continuation context');
    }
    const requestOptionIds = request.options.map((option) => option.id);
    if (
      schema.option_ids.length !== requestOptionIds.length ||
      schema.option_ids.some((id, index) => id !== requestOptionIds[index])
    ) {
      throw new Error('Mismatched HITL pending input schema');
    }
    if (!skipped && selectedOptionIds.length > schema.max_selected) {
      throw new Error('Too many HITL options selected');
    }
    if (!skipped && selectedOptionIds.length < schema.min_selected) {
      throw new Error('Too few HITL options selected');
    }
    if (skipped && !schema.allow_skip) {
      throw new Error('Skipping this HITL input is not allowed');
    }
    if (freeText && !schema.allow_free_text) {
      throw new Error('Free text is not allowed for this HITL input');
    }

    values = skipped
      ? [{ kind: 'skip', value: true }]
      : [
          ...selectedOptionIds.map((optionId): HitlAnswerValue => {
            const option = optionById.get(optionId) as UserInputOption;
            return {
              kind: 'option',
              option_id: optionId,
              value: option.value ?? optionId,
            };
          }),
          ...(freeText
            ? ([{ kind: 'free_text', value: freeText }] satisfies HitlAnswerValue[])
            : []),
        ];
  }
  return {
    request_id: request.request_id,
    continuation_id: request.continuation_id ?? null,
    values,
    selected_option_ids: selectedOptionIds,
    selected_labels: resolveSelectedLabels(request, selectedOptionIds),
    question: request.question,
    resume_query: request.resume_query ?? null,
    free_text: freeText || null,
    skipped,
  };
}

/**
 * 사용자 메시지로 보낼 텍스트.
 *
 * 라벨과 자유입력을 그대로 이어붙인다. 이 문자열이 대화 히스토리에 남아
 * 다음 턴 LLM 이 "무엇을 골랐는지" 를 자연스럽게 읽는다.
 */
export function renderHitlAnswerText(
  answer: HitlAnswer,
  labels: { skipped: string; join?: string },
): string {
  if (answer.skipped) return labels.skipped;
  const parts = [...answer.selected_labels];
  const freeText = (answer.free_text ?? '').trim();
  if (freeText) parts.push(freeText);
  return parts.join(labels.join ?? ', ');
}

/** 응답이 전송 가능한 상태인지 (빈 응답 차단). */
export function isSubmittableSelection(selection: HitlSelection): boolean {
  if (selection.skipped) return true;
  if (selection.selectedOptionIds.length > 0) return true;
  return (selection.freeText ?? '').trim().length > 0;
}

const HITL_RESPONSE_ACCEPTED_SCHEMA = 'hitl-response-accepted/v1' as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Strictly parse the server-owned post-reservation ACK payload. */
export function parseHitlResponseAcceptedDetail(
  value: unknown,
): HitlResponseAcceptedDetail | null {
  if (!isRecord(value)) return null;
  const keys = Object.keys(value);
  if (
    keys.length !== 3 ||
    !keys.every((key) =>
      key === 'schema' || key === 'request_id' || key === 'continuation_id',
    )
  ) {
    return null;
  }
  if (value.schema !== HITL_RESPONSE_ACCEPTED_SCHEMA) return null;
  if (typeof value.request_id !== 'string' || value.request_id.length === 0) {
    return null;
  }
  if (
    typeof value.continuation_id !== 'string' ||
    value.continuation_id.length === 0
  ) {
    return null;
  }
  return {
    schema: HITL_RESPONSE_ACCEPTED_SCHEMA,
    request_id: value.request_id,
    continuation_id: value.continuation_id,
  };
}

/**
 * Return an acceptance receipt only for the exact submitted continuation.
 * There is intentionally no fallback based on text, event order, or HTTP code.
 */
export function matchHitlResponseAccepted(
  data: AgentInProgressData,
  submittedAnswer: HitlAnswer,
): HitlResponseAcceptedDetail | null {
  if (data.event !== 'hitl_response_accepted') return null;
  const detail = parseHitlResponseAcceptedDetail(data.detail);
  if (!detail) return null;
  if (detail.request_id !== submittedAnswer.request_id) return null;
  if (
    typeof submittedAnswer.continuation_id !== 'string' ||
    detail.continuation_id !== submittedAnswer.continuation_id
  ) {
    return null;
  }
  return detail;
}

/** 서버 ACK로 확정된 답변이 저장된 질문과 정확히 같은 continuation인지 확인한다. */
export function doesHitlAnswerSettleRequest(
  answer: HitlAnswer | null | undefined,
  request: UserInputRequest | null | undefined,
): boolean {
  if (!answer || !request) return false;
  if (answer.request_id !== request.request_id) return false;
  return (
    typeof answer.continuation_id === 'string' &&
    typeof request.continuation_id === 'string' &&
    answer.continuation_id === request.continuation_id
  );
}
